import { useEffect, useMemo, useRef, useState } from "react";

export type TieredSelectTier = {
  label: string;
  options: { value: string; label: string }[];
};

export default function TieredSelect({
  value,
  placeholder,
  tiers,
  onChange,
}: {
  value: string;
  placeholder: string;
  tiers: TieredSelectTier[];
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const valueLabel = useMemo(() => {
    for (const tier of tiers) {
      const hit = tier.options.find((o) => o.value === value);
      if (hit) return hit.label;
    }
    return "";
  }, [tiers, value]);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      const el = rootRef.current;
      if (!el) return;
      if (!el.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full min-h-[54px] flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-[14px] bg-white cursor-pointer transition-all shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${
          open
            ? "border border-emerald-300 ring-4 ring-emerald-100/70"
            : "border border-slate-200 hover:border-slate-300"
        }`}
        aria-expanded={open}
      >
        <span className={`min-w-0 truncate text-left ${valueLabel ? "text-slate-800 font-medium" : "text-slate-400"}`}>
          {valueLabel || placeholder}
        </span>
        <span className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-100 shrink-0">
          <i className={`ri-arrow-down-s-line text-slate-500 text-xl transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-3 w-full overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.14)]">
          <div className="max-h-[60vh] overflow-auto py-3">
            {tiers.map((tier) => (
              <div key={tier.label} className="px-3 py-1.5">
                <div className="px-2 pt-2 pb-1.5 text-[12px] font-bold text-slate-500">
                  {tier.label}
                </div>
                <div className="rounded-2xl bg-slate-50/70 p-1.5">
                  {tier.options.map((opt) => {
                    const active = opt.value === value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          onChange(opt.value);
                          setOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2.5 rounded-xl text-[14px] transition-colors ${
                          active
                            ? "bg-white text-emerald-900 shadow-sm ring-1 ring-emerald-200"
                            : "hover:bg-white text-slate-700"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
